import { FC, ReactElement, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Page } from '../../../components';
import { NotFound } from '../../../containers';
import { MeilisearchAttribute } from '../../../entities/@api/Meilisearch';
import { ProductCategoryParams } from '../../../entities/@products/ProductCategory/ProductCategory';
import { transformProductCategoryToNamesArray } from '../../../entities/@products/ProductCategory/ProductCategoryTransformers';
import { ProductListCallToActionInterface } from '../../../entities/@products/ProductListCallToAction/ProductListCallToAction';
import { generateMeilisearchFilterQuery } from '../../../entities/@search/Meilisearch/MeilisearchRequests';
import { useClientEffect } from '../../../hooks';
import { fetchProductCategoryPageData } from '../../../redux/@products/productCategory/productCategoryActions';
import { setActiveProductCategory, setProductCategoryReplacementUrl } from '../../../redux/@products/productCategory/productCategorySlice';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';

interface ConnectedProductCategoryChildProps {
    baseFilter: string;
    childCategories: string[];
    callToActions: ProductListCallToActionInterface[];
}

interface ConnectedProductCategoryWrapperProps {
    children: (childProps: ConnectedProductCategoryChildProps) => ReactElement;
}

export const ConnectedProductCategoryWrapper: FC<ConnectedProductCategoryWrapperProps> = ({
    children,
}): ReactElement => {
    const { slug, subCategory, subSubCategory } = useParams<ProductCategoryParams>();
    const navigate = useNavigate();

    const dispatch = useTypedDispatch();

    const productCategories = useTypedSelector(state => state.productCategoriesSlice.productCategories);

    const hasFetched = useTypedSelector(state => state.productCategorySlice.hasFetched);
    const activeProductCategory = useTypedSelector(state => state.productCategorySlice.activeProductCategory);
    const productCategoryReplacementUrl = useTypedSelector(state => state.productCategorySlice.productCategoryReplacementUrl);
    const pageData = useTypedSelector(state => state.productCategorySlice.pageData);

    useClientEffect((): void => {
        if (productCategories.length > 0 && slug) {
            dispatch(fetchProductCategoryPageData({ slug, subCategory, subSubCategory }));
        }

        // Forget active product category if slug doesn't match, to prevent visible products from another category during load
        if (activeProductCategory) {
            const slugs = [slug, subCategory, subSubCategory];

            if (!slugs.includes(activeProductCategory.slug)) {
                dispatch(setActiveProductCategory(undefined));
            }
        }
    }, [productCategories, slug, subCategory, subSubCategory]);

    useEffect((): void => {
        if (productCategoryReplacementUrl) {
            dispatch(setProductCategoryReplacementUrl(undefined));

            navigate(productCategoryReplacementUrl, { replace: true });
        }
    }, [productCategoryReplacementUrl]);

    if (hasFetched && !activeProductCategory) {
        return (
            <Page>
                <NotFound />
            </Page>
        );
    }

    const baseFilter = activeProductCategory
        ? generateMeilisearchFilterQuery(MeilisearchAttribute.categories, activeProductCategory.name)
        : '';

    const childCategories = activeProductCategory?.children
        ? activeProductCategory.children.flatMap(transformProductCategoryToNamesArray)
        : [];

    return (
        <div>
            {children({
                baseFilter,
                childCategories,
                callToActions: pageData?.callToActions || [],
            })}
        </div>
    );
};
